import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { checkApiStatus, checkDbStatus } from './services/statusService';
import AppRoutes from './AppRoutes';
import ErrorBoundary from './components/ErrorBoundary';
import AppToolbar from './components/AppToolbar';
import AppFooter from './components/AppFooter';
import SideMenu from './components/SideMenu';
import DocumentWizardDialog from './components/DocumentWizardDialog';
import ConfermaGlobale from './components/ConfermaGlobale';
import { CssBaseline, Box, Snackbar, Alert } from '@mui/material';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import { useOverlayWatchdog } from './hooks/useOverlayWatchdog';

const AppContent: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [apiStatus, setApiStatus] = useState(false);
  const [dbStatus, setDbStatus] = useState(false);
  const { isAuthenticated, notificationError, setNotificationError } = useAuth();
  const location = useLocation();

  const isPublicRoute = location.pathname.startsWith('/public');

  // segnala in console se qualcosa copre il pulsante del menu
  useOverlayWatchdog();

  useEffect(() => {
    const checkStatus = async () => {
      const apiOk = await checkApiStatus();
      setApiStatus(apiOk);
      if (apiOk) {
        setDbStatus(await checkDbStatus());
      } else {
        setDbStatus(false);
      }
    };

    const interval = setInterval(checkStatus, 10000); // Check every 10 seconds
    checkStatus();

    return () => clearInterval(interval);
  }, []);

  const toggleDrawer = (open: boolean) => (
    event: React.KeyboardEvent | React.MouseEvent
  ) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setNotificationError(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {!isPublicRoute && (
        <AppToolbar
          drawerOpen={drawerOpen}
          setDrawerOpen={setDrawerOpen}
          apiStatus={apiStatus}
          dbStatus={dbStatus}
        />
      )}
      {isAuthenticated && !isPublicRoute && (
        <SideMenu drawerOpen={drawerOpen} toggleDrawer={toggleDrawer} apiStatus={apiStatus} dbStatus={dbStatus} />
      )}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          flexShrink: 0,
          minHeight: '95vh',
          pt: isPublicRoute ? 0 : '64px', // For the fixed AppBar
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ErrorBoundary>
          <AppRoutes />
        </ErrorBoundary>
      </Box>
      {isAuthenticated && !isPublicRoute && location.pathname !== '/login' && (
        <AppFooter apiStatus={apiStatus} dbStatus={dbStatus} compatto />
      )}
      {isAuthenticated && !isPublicRoute && <DocumentWizardDialog />}
      <ConfermaGlobale />
      <Snackbar open={!!notificationError} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity="error" sx={{ width: '100%' }}>
          {notificationError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const App: React.FC = () => {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <CssBaseline />
      <AppContent />
    </Router>
  );
};

export default App;